import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { colors, fonts, spacing } from '../theme';
import Card from '../components/Card';
import Button from '../components/Button';
import Table from '../components/Table';
import EmptyState from '../components/EmptyState';
import SkeletonLoader from '../components/SkeletonLoader';
import { supabase } from '../lib/supabase';
import { useMatching } from './hooks/useMatching';

function MatchingAccountProfile() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [identities, setIdentities] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [billingEvents, setBillingEvents] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [billingPreview, setBillingPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const { syncNwraStatus, runWithToast } = useMatching();

  const load = async () => {
    if (!supabase || !clientId) return;
    setLoading(true);
    try {
      const { data: clientRow } = await supabase
        .from('clients')
        .select('*')
        .eq('id', clientId)
        .single();
      setClient(clientRow);

      const [{ data: idRows }, { data: incRows }, { data: billRows }, { data: auditRows }] =
        await Promise.all([
          supabase.from('external_identities').select('*').eq('client_id', clientId),
          supabase
            .from('incoming_records')
            .select('*')
            .eq('matched_client_id', clientId)
            .order('occurred_at', { ascending: false })
            .limit(50),
          supabase
            .from('billing_events')
            .select('*')
            .eq('client_id', clientId)
            .order('occurred_at', { ascending: false })
            .limit(25),
          supabase
            .from('audit_events')
            .select('*')
            .eq('target_type', 'client')
            .eq('target_id', String(clientId))
            .order('created_at', { ascending: false })
            .limit(25),
        ]);

      setIdentities(idRows || []);
      setIncoming(incRows || []);
      setBillingEvents(billRows || []);
      setAuditEvents(auditRows || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const loadBilling = async () => {
    if (!client) return;
    const { data, error } = await supabase.functions.invoke('stripe-billing', {
      body: { client_id: client.id, email: client.login_email || client.owner_email },
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    setBillingPreview(data);
  };

  const handleSyncNwra = async () => {
    await runWithToast('NWRA status synced', () => syncNwraStatus(client.id));
    load();
  };

  if (loading) {
    return <SkeletonLoader variant="card" count={3} />;
  }

  if (!client) {
    return (
      <EmptyState
        title="Account not found"
        description="This account may have been removed."
        actionLabel="Back to people"
        onAction={() => navigate('/admin/matching/people')}
      />
    );
  }

  const nwraIdentity = identities.find((i) => i.source === 'nwra' && i.is_primary);
  const stripeIdentities = identities.filter((i) => i.source === 'stripe');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: spacing.sm }}>
        <div>
          <Link to="/admin/matching/people" style={{ color: colors.blue, fontSize: '0.9rem' }}>
            ← People
          </Link>
          <h2 style={{ fontFamily: fonts.heading, margin: `${spacing.sm} 0 0` }}>
            {client.business_name || client.owner_name || client.login_email || `Client #${client.id}`}
          </h2>
        </div>
        <Link to={`/admin/clients/${client.id}`}>
          <Button variant="outline" size="sm">
            Open in Clients
          </Button>
        </Link>
      </div>

      <Card title="Identity">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: spacing.md, fontSize: '0.9rem' }}>
          <div><strong>Email:</strong> {client.login_email || client.owner_email || '—'}</div>
          <div><strong>State:</strong> {client.account_state || 'active'}</div>
          <div><strong>Created:</strong> {client.created_at ? new Date(client.created_at).toLocaleDateString() : '—'}</div>
          <div><strong>Activated:</strong> {client.activated_at ? new Date(client.activated_at).toLocaleDateString() : '—'}</div>
        </div>
      </Card>

      <Card title="NWRA">
        <div style={{ fontSize: '0.9rem', marginBottom: spacing.md }}>
          <div><strong>Company ID:</strong> {client.nwra_company_id || nwraIdentity?.external_id || '—'}</div>
          <div><strong>Last sync:</strong> {client.nwra_synced_at ? new Date(client.nwra_synced_at).toLocaleString() : '—'}</div>
          {client.nwra_deadlines && (
            <pre style={{ marginTop: spacing.sm, fontSize: '0.75rem', backgroundColor: colors.gray100, padding: spacing.sm, borderRadius: 8, overflow: 'auto' }}>
              {JSON.stringify(client.nwra_deadlines, null, 2)}
            </pre>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={handleSyncNwra} disabled={!client.nwra_company_id}>
          Sync NWRA status
        </Button>
      </Card>

      <Card title="Stripe">
        <div style={{ fontSize: '0.9rem', marginBottom: spacing.md }}>
          <div><strong>Direct customer:</strong> {client.stripe_customer_id_direct || '—'}</div>
          <div><strong>JotForm customer:</strong> {client.stripe_customer_id_jotform || '—'}</div>
          {stripeIdentities.map((i) => (
            <div key={i.id}><strong>Linked:</strong> {i.external_id}</div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={loadBilling}>
          Load subscription & payments
        </Button>
        {billingPreview && (
          <pre style={{ marginTop: spacing.md, fontSize: '0.75rem', maxHeight: 200, overflow: 'auto', backgroundColor: colors.gray100, padding: spacing.sm, borderRadius: 8 }}>
            {JSON.stringify(billingPreview, null, 2)}
          </pre>
        )}
        {billingEvents.length > 0 && (
          <div style={{ marginTop: spacing.md }}>
            <h4 style={{ fontFamily: fonts.heading, fontSize: '0.95rem' }}>Payment history (cached)</h4>
            <Table
              columns={[
                { key: 'occurred_at', label: 'Date', render: (r) => new Date(r.occurred_at).toLocaleDateString() },
                { key: 'description', label: 'Description' },
                { key: 'amount_cents', label: 'Amount', render: (r) => r.amount_cents != null ? `$${(r.amount_cents / 100).toFixed(2)}` : '—' },
                { key: 'status', label: 'Status' },
              ]}
              data={billingEvents}
              keyField="id"
              responsive
            />
          </div>
        )}
      </Card>

      <Card title="Linked incoming events">
        {incoming.length === 0 ? (
          <EmptyState title="No linked events" description="" />
        ) : (
          <Table
            columns={[
              { key: 'source', label: 'Source' },
              { key: 'type', label: 'Type' },
              { key: 'status', label: 'Status' },
              { key: 'occurred_at', label: 'When', render: (r) => r.occurred_at ? new Date(r.occurred_at).toLocaleString() : '—' },
            ]}
            data={incoming}
            keyField="id"
            responsive
          />
        )}
      </Card>

      <Card title="Audit (this account)">
        {auditEvents.length === 0 ? (
          <p style={{ color: colors.midGray, fontSize: '0.9rem' }}>No audit events for this account yet.</p>
        ) : (
          <Table
            columns={[
              { key: 'created_at', label: 'When', render: (r) => new Date(r.created_at).toLocaleString() },
              { key: 'action', label: 'Action' },
              { key: 'metadata', label: 'Details', render: (r) => JSON.stringify(r.metadata || {}) },
            ]}
            data={auditEvents}
            keyField="id"
            responsive
          />
        )}
      </Card>
    </div>
  );
}

export default MatchingAccountProfile;
